import { Euler, Group, Vector2, Vector3 } from "three";
import { RenderOrder } from "@modules/rendering/RenderOrder";
import { GLOBAL_UNIFORMS } from "@modules/rendering/shader/GlobalUniforms";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { renderManager } from "@modules/xr/RenderManager";
import { modelPool } from "@modules/model";
import { GizmoButton } from "./GizmoButton";
/// Temp variables to reduce GC ///
const TEMP_VEC2_1 = new Vector2();
const TEMP_VEC2_2 = new Vector2();
const TEMP_EULER = new Euler();
/**
 * We describe all buttons here for convenience to automatically generate them in the gizmo button group.
 */
var GizmoButtonType;
(function (GizmoButtonType) {
    GizmoButtonType[GizmoButtonType["TRANSLATE"] = 0] = "TRANSLATE";
    GizmoButtonType[GizmoButtonType["ROTATE"] = 1] = "ROTATE";
    GizmoButtonType[GizmoButtonType["SCALE"] = 2] = "SCALE";
    GizmoButtonType[GizmoButtonType["COORDINATE_SPACE"] = 3] = "COORDINATE_SPACE";
    GizmoButtonType[GizmoButtonType["RESET"] = 4] = "RESET";
})(GizmoButtonType || (GizmoButtonType = {}));
const GIZMO_BUTTON_TYPES = [GizmoButtonType.TRANSLATE, GizmoButtonType.ROTATE, GizmoButtonType.SCALE, GizmoButtonType.COORDINATE_SPACE, GizmoButtonType.RESET];
const GIZMO_BUTTON_DESCRIPTIONS = {
    [GizmoButtonType.TRANSLATE]: {
        action: "translate",
        tooltip: "Translation Mode",
        textures: [GLOBAL_UNIFORMS.translateTexture],
        offset: 1.5
    },
    [GizmoButtonType.ROTATE]: {
        action: "rotate",
        tooltip: "Rotation Mode",
        textures: [GLOBAL_UNIFORMS.rotateTexture],
        offset: 2.75
    },
    [GizmoButtonType.SCALE]: {
        action: "scale",
        tooltip: "Scale Mode",
        textures: [GLOBAL_UNIFORMS.scaleTexture],
        offset: 4.0
    },
    [GizmoButtonType.COORDINATE_SPACE]: {
        action: "world",
        tooltip: "Toggle World and Local Coordinate System",
        textures: [GLOBAL_UNIFORMS.transformGlobalTexture, GLOBAL_UNIFORMS.transformLocalTexture],
        offset: 0
    },
    [GizmoButtonType.RESET]: {
        action: "reset",
        tooltip: "Reset Scale",
        textures: [GLOBAL_UNIFORMS.transformResetTexture],
        offset: 5.25
    }
};
/**
 * The gizmo button group handles the creation and deletion of the gizmo buttons, which toggle world and local
 * coordinates, allows to switch between translation, rotation and scaling as well as a reset button.
 */
export class GizmoButtonGroup {
    constructor(dummyTarget, connection) {
        this.group = new Group();
        this.group.renderOrder = RenderOrder.LASER;
        this.group.visible = false;
        this.dummyTarget = dummyTarget;
        this.connection = connection;
        // Create buttons and add them to the group
        this.buttons = new Map();
        GIZMO_BUTTON_TYPES.forEach((buttonType, buttonIndex) => {
            const buttonDescription = GIZMO_BUTTON_DESCRIPTIONS[buttonType];
            const button = new GizmoButton(buttonDescription.action, buttonDescription.tooltip, buttonDescription.textures, buttonDescription.offset);
            this.buttons.set(buttonType, button);
            this.group.add(button.mesh);
            // Add on before render callback that updates the orientation of the buttons
            // each frame to always face the camera
            if (buttonIndex === 0)
                button.mesh.onBeforeRender = (_1, _2, camera) => {
                    this.updateOrientation(camera);
                };
        });
    }
    show(isScalable) {
        var _a, _b;
        // Update scaling button states
        (_a = this.buttons.get(GizmoButtonType.SCALE)) === null || _a === void 0 ? void 0 : _a.setActive(isScalable);
        (_b = this.buttons.get(GizmoButtonType.RESET)) === null || _b === void 0 ? void 0 : _b.setActive(isScalable);
        this.setTransformMode("translate");
        if (!this.isVisible()) {
            const messages = [];
            this.buttons.forEach((button) => {
                button.createButtonModel(messages);
            });
            this.connection.sendMultiple(messages);
        }
        this.group.visible = true;
    }
    hide() {
        if (this.isVisible()) {
            const messages = [];
            this.buttons.forEach((button) => {
                button.removeButtonModel(messages);
            });
            this.connection.sendMultiple(messages);
        }
        this.group.visible = false;
    }
    update(isScalable) {
        this.hide();
        this.show(isScalable);
    }
    setTransformMode(transformMode) {
        var _a, _b, _c;
        (_a = this.buttons.get(GizmoButtonType.TRANSLATE)) === null || _a === void 0 ? void 0 : _a.setSelected(transformMode === "translate");
        (_b = this.buttons.get(GizmoButtonType.ROTATE)) === null || _b === void 0 ? void 0 : _b.setSelected(transformMode === "rotate");
        (_c = this.buttons.get(GizmoButtonType.SCALE)) === null || _c === void 0 ? void 0 : _c.setSelected(transformMode === "scale");
    }
    setCoordinateSpace(coordinateSpace) {
        const coordinateSpaceButton = this.buttons.get(GizmoButtonType.COORDINATE_SPACE);
        if (coordinateSpaceButton === undefined)
            return;
        // Change on click interaction of the coordinate space button
        const messages = [];
        coordinateSpaceButton.name = coordinateSpace;
        coordinateSpaceButton.updateInteraction(messages);
        this.connection.sendMultiple(messages);
        // Change appearance
        coordinateSpaceButton.mesh.material = coordinateSpace === "world" ? coordinateSpaceButton.materials[0] : coordinateSpaceButton.materials[1];
    }
    isVisible() {
        return this.group.visible;
    }
    updateOrientation(activeCamera) {
        if (!this.isVisible())
            return;
        const targetPosition = this.dummyTarget.position;
        const offsetScaleFactor = renderManager.isXRPresenting() ? 0.25 : 0.1;
        const indicatorOffset = 0.3;
        // Compute the button scale depending on the distance to the camera
        const cameraPosPlatform = localPlayerContainer.transformPointFromPlatformToWorldCoordinates(activeCamera.position.clone());
        const cameraXZ = TEMP_VEC2_1.set(cameraPosPlatform.x, cameraPosPlatform.z);
        const targetXZ = TEMP_VEC2_2.set(targetPosition.x, targetPosition.z);
        const offset = cameraXZ.distanceTo(targetXZ) * offsetScaleFactor;
        const scale = offset * 1.5 * 0.2;
        // Compute camera up and camera right. we are only interested in rotating the group around the y-axis. This is
        // only relevant in VR where the user can tilt the head
        const cameraOrientation = localPlayerContainer.transformPlatformToWorldOrientation(activeCamera.quaternion.clone());
        this.extractYRotationQuaternion(cameraOrientation, cameraOrientation);
        const cameraRight = new Vector3(-offset, 0, 0).applyQuaternion(cameraOrientation);
        const cameraUp = new Vector3(0, offset, 0).applyQuaternion(cameraOrientation);
        const targetScale = new Vector3(scale, scale, scale);
        GIZMO_BUTTON_TYPES.forEach((buttonType) => {
            var _a;
            const buttonDescription = GIZMO_BUTTON_DESCRIPTIONS[buttonType];
            const offset = indicatorOffset * buttonDescription.offset;
            (_a = this.buttons.get(buttonType)) === null || _a === void 0 ? void 0 : _a.updateTransform(targetPosition, cameraOrientation, targetScale, cameraUp, cameraRight, offset);
        });
        modelPool.flushMessages();
    }
    extractYRotationQuaternion(quaternion, result) {
        // Convert quaternion to Euler angles
        TEMP_EULER.setFromQuaternion(quaternion, "YXZ"); // Use 'YXZ' to prioritize Y
        // Keep only the Y rotation
        TEMP_EULER.x = 0;
        TEMP_EULER.z = 0;
        // Convert back to quaternion
        return result.setFromEuler(TEMP_EULER);
    }
}
